import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Divider,
  Box,
} from '@mui/material';
import { useSidebar } from './Layout';
import {
  Dashboard as DashboardIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Event as EventIcon,
  Payment as PaymentIcon,
  AttachMoney as AttachMoneyIcon,
  Receipt as ReceiptIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { FaHome, FaUsers, FaUserPlus, FaFileInvoiceDollar, FaCog, FaSignOutAlt, FaCheckCircle } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';

const drawerWidth = 240;

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userRole = localStorage.getItem('role');
  const { profileStatus } = useAuth();
  const { sidebarCollapsed, mobileOpen, handleMobileDrawerToggle, setMobileOpen } = useSidebar();

  // Modern menu item styling
  const getMenuItemStyles = (path) => {
    const isActivePath = location.pathname === path;

    return {
    borderRadius: '12px',
    margin: '4px 8px',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      backgroundColor: isActivePath ? '#4e0159' : '#fdd415',
      boxShadow: isActivePath ? '0 4px 16px rgba(78, 1, 89, 0.4)' : '0 2px 8px rgba(78, 1, 89, 0.15)',
      '&.Mui-selected': {
        backgroundColor: '#4e0159 !important',
        boxShadow: '0 4px 16px rgba(78, 1, 89, 0.4)',
      },
      '&:hover': {
        background: '#4e0159',
        transform: 'translateX(4px)',
        boxShadow: '0 4px 16px rgba(78, 1, 89, 0.3)',
      }
    };
  };

  const getIconStyles = (path) => ({
    color: location.pathname === path ? '#ffffff' : '#4e0159',
    transition: 'color 0.3s ease'
  });

  const getTextStyles = (path) => ({
    '& .MuiListItemText-primary': {
      color: location.pathname === path ? '#ffffff' : '#4e0159',
      fontWeight: location.pathname === path ? 600 : 500,
      fontSize: '0.95rem',
      transition: 'all 0.3s ease'
    }
  });

console.log(profileStatus);
 
  const menuItems = [
    { text: 'Profile', icon: <PersonIcon />, path: '/profile' },
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    { text: 'Client Companies', icon: <BusinessIcon />, path: '/company-clients' },
    { text: 'Employees', icon: <PeopleIcon />, path: '/employees' },
    { text: 'Client Visits', icon: <EventIcon />, path: '/client-visits' },
    { text: 'Worker Pay Data', icon: <AttachMoneyIcon />, path: '/worker-pay-data' },
    { text: 'QuickBooks Invoice Data', icon: <ReceiptIcon />, path: '/quickbooks-invoice-data' },
    { text: 'Subscriptions', icon: <PaymentIcon />, path: '/subscriptions' },
  ];

  const isActive = (path) => {
    return location.pathname === path ? 'active' : '';
  };

  const drawer = (
    <div>
      <Toolbar 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center',
          padding: '20px 16px',
          background: '#fdd415',
          borderBottom: '1px solid rgba(78, 1, 89, 0.2)',
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '60%',
            height: '2px',
            background: 'rgba(78, 1, 89, 0.3)',
          }
        }}
      >
        <img 
          src={`${process.env.REACT_APP_BASE_URL}/img/eworxs.png`}  
          alt="EWORXS Logo" 
          style={{ 
            width: '160px',
            height: 'auto',
            filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.3))'
          }} 
        />
      </Toolbar>
      <Divider sx={{ borderColor: 'rgba(78, 1, 89, 0.2)' }} />
      <List sx={{ padding: '8px' }}>
        {menuItems.map((item) => (
          <ListItem
            button
            key={item.text}
            onClick={() => {
              navigate(item.path);
              if (setMobileOpen) setMobileOpen(false);
            }}
            sx={{ 
              display: profileStatus === 'incomplete' && item.text !== 'Profile' ? 'none' : 'flex',
              paddingLeft: '12px',
              paddingRight: '12px',
              ...getMenuItemStyles(item.path)
            }}
          >
            <ListItemIcon sx={getIconStyles(item.path)}>
              {item.icon}
            </ListItemIcon>
            <ListItemText 
              primary={item.text} 
              sx={getTextStyles(item.path)}
            />
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
    >
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleMobileDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            background: '#fdd415',
            boxShadow: '0 8px 32px rgba(78, 1, 89, 0.25)',
            borderRight: 'none',
            overflow: 'hidden'
          },
        }}
      >
        {drawer}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          width: sidebarCollapsed ? 0 : drawerWidth,
          flexShrink: 0,
          transition: 'width 0.3s ease',
          '& .MuiDrawer-paper': {
            width: sidebarCollapsed ? 0 : drawerWidth,
            boxSizing: 'border-box',
            transition: 'width 0.3s ease',
            overflow: sidebarCollapsed ? 'hidden' : 'visible',
            background: '#f1f4fd',
            boxShadow: '0 8px 32px rgba(78, 1, 89, 0.25)',
            borderRight: 'none',
            overflow: 'hidden'
          },
        }}
      >
        <Toolbar 
          sx={{ 
            display: 'flex', 
            justifyContent: 'center',
            padding: '20px 16px',
            background: '#fdd415',
            borderBottom: '1px solid rgba(78, 1, 89, 0.2)',
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: '60%',
              height: '2px',
              background: 'rgba(78, 1, 89, 0.3)',
            }
          }}
        >
          <img 
            src={`${process.env.REACT_APP_BASE_URL}/img/eworxs.png`}
            alt="EWORXS Logo" 
            style={{ 
              width: '160px',
              height: 'auto',
              filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.3))'
            }} 
          />
        </Toolbar>
        <Divider sx={{ borderColor: 'rgba(78, 1, 89, 0.2)' }} />
        <List sx={{ padding: '8px' }}>
          {userRole == 2 && (
            <>
            
              <ListItem 
                    button 
                    component={Link} 
                    to="/profile" 
                    selected={location.pathname === '/profile'}
                sx={{ 
                  px: 1.5,
                  ...getMenuItemStyles('/profile')
                }}
                  >
              
               <ListItemIcon sx={getIconStyles('/profile')}>
                  <PersonIcon />
                </ListItemIcon>
                <ListItemText 
                  primary="Profile" 
                  sx={getTextStyles('/profile')}
                />
              </ListItem>

              {profileStatus !== 'incomplete' && (
                <>
                    <ListItem 
                button 
                component={Link} 
                to="/" 
                selected={location.pathname === '/'}
                sx={{ 
                  px: 1.5,
                  ...getMenuItemStyles('/')
                }}
              >
                  <ListItemIcon sx={getIconStyles('/')}>
                  <DashboardIcon />
                </ListItemIcon>
                <ListItemText primary="Dashboard" sx={getTextStyles('/')} />
              </ListItem>

                  <ListItem 
                    button 
                    component={Link} 
                    to="/company-clients" 
                    selected={location.pathname === '/company-clients'}
                sx={{ 
                  px: 1.5,
                  ...getMenuItemStyles('/company-clients')
                }}
                  >
                    <ListItemIcon sx={getIconStyles('/company-clients')}>
                      <BusinessIcon />
                    </ListItemIcon>
                    <ListItemText primary="Client Companies" sx={getTextStyles('/company-clients')} />
                  </ListItem>

                  <ListItem 
                    button 
                    component={Link} 
                    to="/employees" 
                    selected={location.pathname === '/employees'}
                sx={{ 
                  px: 1.5,
                  ...getMenuItemStyles('/employees')
                }}
                  >
                    <ListItemIcon sx={getIconStyles('/employees')}>
                      <PeopleIcon />
                    </ListItemIcon>
                    <ListItemText primary="Worker List" sx={getTextStyles('/employees')} />
                  </ListItem>

                  <ListItem 
                    button 
                    component={Link} 
                    to="/client-visits" 
                    selected={location.pathname === '/client-visits'}
                sx={{ 
                  px: 1.5,
                  ...getMenuItemStyles('/client-visits')
                }}
                  >
                    <ListItemIcon sx={getIconStyles('/client-visits')}>
                      <EventIcon />
                    </ListItemIcon>
                    <ListItemText primary="Client Visits" sx={getTextStyles('/client-visits')} />
                  </ListItem>

                  <ListItem 
                    button 
                    component={Link} 
                    to="/worker-pay-data" 
                    selected={location.pathname === '/worker-pay-data'}
                sx={{ 
                  px: 1.5,
                  ...getMenuItemStyles('/worker-pay-data')
                }}
                  >
                    <ListItemIcon sx={getIconStyles('/worker-pay-data')}>
                      <AttachMoneyIcon />
                    </ListItemIcon>
                    <ListItemText primary="Worker Pay Data" sx={getTextStyles('/worker-pay-data')} />
                  </ListItem>

                  <ListItem 
                    button 
                    component={Link} 
                    to="/quickbooks-invoice-data" 
                    selected={location.pathname === '/quickbooks-invoice-data'}
                sx={{ 
                  px: 1.5,
                  ...getMenuItemStyles('/quickbooks-invoice-data')
                }}
                  >
                    <ListItemIcon sx={getIconStyles('/quickbooks-invoice-data')}>
                      <ReceiptIcon />
                    </ListItemIcon>
                    <ListItemText primary="QuickBooks Invoice Data" sx={getTextStyles('/quickbooks-invoice-data')} />
                  </ListItem>

                  <ListItem 
                    button 
                    component={Link} 
                    to="/subscriptions" 
                    selected={location.pathname === '/subscriptions'}
                sx={{ 
                  px: 1.5,
                  ...getMenuItemStyles('/subscriptions')
                }}
                  >
                    <ListItemIcon sx={getIconStyles('/subscriptions')}>
                      <PaymentIcon />
                    </ListItemIcon>
                    <ListItemText primary="Subscriptions" sx={getTextStyles('/subscriptions')} />
                  </ListItem>
                </>
              )}
            </>
          )}
          {userRole == 1 && (
            <>
              <ListItem 
                button 
                component={Link} 
                to="/companies" 
                selected={location.pathname === '/companies'}
                sx={{ 
                  px: 1.5,
                  ...getMenuItemStyles('/companies')
                }}
              >
                <ListItemIcon sx={getIconStyles('/companies')}>
                  <FaCheckCircle />
                </ListItemIcon>
                <ListItemText primary="Companies" sx={getTextStyles('/companies')} />
              </ListItem>
              <ListItem 
                button 
                component={Link} 
                to="/plans" 
                selected={location.pathname === '/plans'}
                sx={{ 
                  px: 1.5,
                  ...getMenuItemStyles('/plans')
                }}
              >
                <ListItemIcon sx={getIconStyles('/plans')}>
                  <FaCheckCircle />
                </ListItemIcon>
                <ListItemText primary="Plans" sx={getTextStyles('/plans')} />
              </ListItem>
            </>
          )}
        </List>
      </Drawer>
    </Box>
  );
};

export default Sidebar; 